import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND, columnTemplateStateSchema } from 'src/features/aggregated-risk/config';
import { AggregatedRiskSelfOwnedModel } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskSelfOwnedModel';
import type { AggregatedRiskSelfOwnedEventBus } from 'src/models/AggregatedRiskWidgetModel/AggregatedRiskSelfOwnedModel';
import { PlatformSelectionModel, platformSelectionStateSchema } from 'src/models/PlatformSelectionModel';
import type { PlatformSelectionModelEventBus } from 'src/models/PlatformSelectionModel';
import { stickyFilterStateSchema } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import type { AggregatedRiskTableFilters } from 'src/queries/aggregatedRiskTables';
import { EMPTY_AGGREGATED_RISK_TABLE_FILTERS } from 'src/queries/aggregatedRiskTables';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';
import type { z } from 'zod';

export const platformsWidgetStateSchema = stickyFilterStateSchema.merge(platformSelectionStateSchema).extend({
    columnTemplate: columnTemplateStateSchema,
});

export type PlatformsWidgetState = z.infer<typeof platformsWidgetStateSchema>;

export type PlatformsSelfOwnedEventBus = EventBus<
    EventBusSignals<AggregatedRiskSelfOwnedEventBus> & EventBusSignals<PlatformSelectionModelEventBus>,
    EventBusEvents<AggregatedRiskSelfOwnedEventBus> & EventBusEvents<PlatformSelectionModelEventBus>
>;

interface PlatformsSelfOwnedModelInit {
    container: WidgetHostContainer;
    tabId: WidgetId;
    persisted: PlatformsWidgetState;
    logger: Logger;
    queryClient: ReactBindings['QueryClient'];
    slugMapService: ReactBindings['SlugMapService'];
    table: TableModel;
}

interface PlatformsSelfOwnedModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: PlatformsSelfOwnedEventBus;
    readonly tableFilters: ReadonlySignal<AggregatedRiskTableFilters>;
    readonly selectedPlatforms: ReadonlySignal<string[]>;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly groupByIndicatorIds: ReadonlySignal<readonly string[]>;
    readonly columnTemplateError: ReadonlySignal<Error | null>;
    toJSON(): PlatformsWidgetState;
    markClean(): void;
}

export const PlatformsSelfOwnedModel: ModelConstructor<PlatformsSelfOwnedModelShape, [PlatformsSelfOwnedModelInit]> =
    createModel<PlatformsSelfOwnedModelShape, [PlatformsSelfOwnedModelInit]>((init: PlatformsSelfOwnedModelInit) => {
        const aggregatedRiskModel = new AggregatedRiskSelfOwnedModel({
            ...init,
            config: AGGREGATED_RISK_WIDGET_CONFIG_BY_KIND.platforms,
        });
        const platformSelectionModel = new PlatformSelectionModel({ persisted: init.persisted });
        const dirty = computed(() => aggregatedRiskModel.dirty.value || platformSelectionModel.dirty.value);
        const eventBus: PlatformsSelfOwnedEventBus = MergedEventBus.from<AggregatedRiskSelfOwnedEventBus>(
            aggregatedRiskModel.eventBus,
        ).merge<PlatformSelectionModelEventBus>(platformSelectionModel.eventBus);
        const tableFilters = computed<AggregatedRiskTableFilters>(() => ({
            ...EMPTY_AGGREGATED_RISK_TABLE_FILTERS,
            platformIds: platformSelectionModel.selectedPlatforms.value,
        }));

        return {
            dirty,
            eventBus,
            tableFilters,
            selectedPlatforms: platformSelectionModel.selectedPlatforms,
            columnTemplate: aggregatedRiskModel.columnTemplate,
            groupByIndicatorIds: aggregatedRiskModel.groupByIndicatorIds,
            columnTemplateError: aggregatedRiskModel.columnTemplateError,
            toJSON(): PlatformsWidgetState {
                return {
                    ...aggregatedRiskModel.toJSON(),
                    ...platformSelectionModel.toJSON(),
                };
            },
            markClean(): void {
                aggregatedRiskModel.markClean();
                platformSelectionModel.markClean();
            },
        };
    });

export type PlatformsSelfOwnedModel = Model<PlatformsSelfOwnedModelShape>;
